import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Card } from '@/common/components/Card/Card';
import { Heading } from '@/common/components/Heading/Heading';
import { Label } from '@/common/components/Label/Label';
import { useModal } from '@/common/components/Modal/Modal.utils';
import { ModalDiscount } from '@/common/components/ModalDiscount/ModalDiscount';
import { Stack } from '@/common/components/Stack/Stack';
import { Translate } from '@/common/components/Translate/Translate';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { IDiscount } from '@/common/integrations/menhir/employee/discounts/discounts.types';
import {
  isAmountDiscount,
  isPercentageDiscount,
} from '@/common/utils/discounts';
import { MoneyUtils } from '@coverflex-tech/hypernova-utils';
import { Image } from 'expo-image';
import { styles } from './CardDiscount.styles';

export function CardDiscount({ discount, className }: TCardDiscountProps) {
  const { t } = useTranslation();
  const modalDiscount = useModal();

  const discountValue = useMemo(() => {
    if (isPercentageDiscount(discount)) {
      return discount.percentage !== '0'
        ? `${discount.percentage}%`
        : undefined;
    } else if (isAmountDiscount(discount)) {
      return discount.amount.amount
        ? MoneyUtils.moneyToString(discount.amount, { integer: true })
        : undefined;
    }
  }, [discount]);

  const onOpenDiscountDetails = () => {
    modalDiscount.setIsOpen(true);
    AnalyticsService.trackCustomEvent('discount_details_button_clicked', {
      category: discount.category,
      partner: discount.partnerName,
    });
  };

  return (
    <>
      {modalDiscount.isOpen && (
        <ModalDiscount
          discount={discount}
          onClose={() => modalDiscount.setIsOpen(false)}
        />
      )}
      <Card className={className} onPress={onOpenDiscountDetails}>
        <Stack horizontal gap="md" align="center" className={styles.wrapper}>
          <Stack align="center">
            <Image
              source={{ uri: discount.logoUrl }}
              alt={discount.partnerName}
              className={styles.icon}
              contentFit="contain"
              transition={250}
            />
          </Stack>
          <Stack shrink grow gap="xs">
            <Heading level={5}>
              {t(`wallet.discounts.${discount.partnerName}.name`)}
            </Heading>
            <BodyText size="sm" color="neutral20" numberOfLines={2}>
              <Translate
                i18nKey={`wallet.discounts.${discount.partnerName}.description_short`}
                values={{ discount_value: discountValue }}
              />
            </BodyText>
          </Stack>
          {discountValue && (
            <Stack className={styles.discount} align="center" justify="center">
              <Label size="sm" bold>
                {discountValue}
              </Label>
            </Stack>
          )}
        </Stack>
      </Card>
    </>
  );
}

export type TCardDiscountProps = {
  discount: IDiscount;
  className?: string;
};
